import * as React from "react"
import { connect, Provider } from "react-redux"
import trans from "trans"

import {
    css,
    Icon,
    Grid,
    GridCell,
    Theme,
    Badge,
    Button,
    modal,
    Div,
} from "@bdfx/mergado-ui-kit"

import {
    MERGADO_APP, getInpaintingGroups, isGodModeOn,
    saveInvertMaskObjects,
} from "app/helpers/api";

import Mergado from "app/helpers/mergado";

import { FiltersFormType } from "../types/filtertypes";
import { ViewTableType } from "products/containers";
import SectionLoader from "app/components/section-loader";

import { Project, SettingsFormType, Template, AlternativeTemplatesLevels, AlternativeTemplate } from 'app/types/model-types';
import { ImgModeType } from "./product-table";
import { URL } from "app/config/api-config";

import { DarkGrayColor } from "app/helpers/colors";
import { AlternativeImageTypeWithWatermarkMasks, DiagnosticsType, ExtendedProductType, FindSimilarType, ImageType, InpaintingGroupType, ProductGeneral, ProductTemplateType, RENDERED_TYPE_AUTOWMREMOVE, RENDERED_TYPE_BGREMOVE, RENDERED_TYPE_INPAINTING, RENDERED_TYPE_OUTPAINTING, RENDERED_TYPE_UPSCALE } from "../types";
import { isJson } from "app/helpers/other";
import { getAddOutpaintedSuffix, getAltTemplateLevelsByLevel, getAltTemplatesByLevels, getInpaintingGroupPosition, getNewSize } from "shape-edit/forms/helpers";
import { allowSuperadminForUsers } from "template-edit/components/helper";
import { selectFormValues } from "app/reducers/form";
import { ShowGroupType } from "diagnostics/diagnostics";
import VisualSimilarityRender from "./visual-similarity-render";
import { getTemplateTypeTranslated, TemplateBadges } from "template-list/template-badges";
import { setFindSimilar, setProductsToStore } from "products/reducer";
import FindSimilarButton from "./find-similar-button";
import { WatermarksRenderSvgObjects } from "./render-objects-helper";
import { find, forEach, template } from "lodash";
import { RouterType, withRouter } from "app/components/routes";
import InvertMaskMarker, { InvertMaskModeType } from "./invert-mask-marker";
import { ObjectsType } from "./watermark-marker-common";
import { AxiosResponse } from "axios";
import store from "store";
import { ID, ReduxList } from "app/types/app-types";
import { AltTemplatesReduxList } from "template-edit/reducers";
import { SizeItem } from "settings/components/sizes-settings";
import { setInpaintingGroupsToStore } from "watermark-groups/reducers";
import { renderAltLevels } from "shape-edit/forms/fields";

const iconSize = 20

interface Props {
    products: ReduxList<DiagnosticsType>
    router: RouterType
    inpaintingGroups: ReduxList<InpaintingGroupType[]>
    alternativeTemplatesLevels: ReduxList<AlternativeTemplatesLevels>
    alternativeTemplates: AltTemplatesReduxList
    sizes: ReduxList<SizeItem>
    isLoading: boolean
    viewTable: ViewTableType
    project: Project
    paginator: JSX.Element
    handleSortBy: Function
    filtersForm: FiltersFormType
    settingsForm: SettingsFormType
    publishSelected: Function
    changeSetByUser: Function
    showModal: Function
    getEmptyFiltersForm: Function
    updateWatermarkMasksFromDb: Function
    imgMode: ImgModeType
    findSimilar: FindSimilarType
    setFindSimilar: typeof setFindSimilar
    setProductsToStore: typeof setProductsToStore
    setInpaintingGroupsToStore: typeof setInpaintingGroupsToStore
}

interface State {
    clipboardText: string
}

class ProductTableGrid extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            clipboardText: null,
        }
    }

    componentDidMount(): void {
        if (!this.props.inpaintingGroups.isLoaded) {
            getInpaintingGroups()
                .then((response: AxiosResponse<InpaintingGroupType[]>) => {
                    this.props.setInpaintingGroupsToStore(response.data)
                })
                .catch(e => {
                    console.error('cannot get inpainting groups', e)
                })
        }
    }

    updateUserSetObjects = (isAlt: boolean, urlHash: string, productId: ID, imageOrAltImageId: ID, level: number, idx: number, objects: ObjectsType, mode: InvertMaskModeType = null, maskHash: string) => {
        let updatedProductsData = { ...this.props.products.data }
        if (isAlt) {
            updatedProductsData.products.data[idx].alternative_images[level].alt_watermark_masks.mask_hash = maskHash
            updatedProductsData.products.data[idx].alternative_images[level].alt_watermark_masks.objects = objects
        } else {
            updatedProductsData.products.data[idx].invert_mask_hash = maskHash
            updatedProductsData.products.data[idx].masks_user_set_objects = objects
        }

        this.props.setProductsToStore(updatedProductsData)
    }

    getImageByImgMode = (product: ExtendedProductType, idx: number): JSX.Element => {
        const { imgMode, viewTable } = this.props
        const productId = product.product_id
        const timeStamp = new Date(product.rendered_at).getTime()
        const nowTimestamp = `${new Date().getTime()}`.substring(8)

        const newSize = getNewSize(product.image_width, product.image_height, { width: product.size_width, height: product.size_height }, product.template_overscan, !!product.template_outpaint_over)
        const addSuffix = getAddOutpaintedSuffix(product.template_outpaint_over, product.template_overscan, product.template_translate_x, product.template_translate_y)
        const sizeString = newSize && product.size_width && product.size_height ? `${newSize?.width}x${newSize?.height}${addSuffix}` : null
        const showAll = this.props.router.location.search.includes('showAll')

        const previewUrl = `${URL.laravelBase}images?template_id=${product.template_id}&product_id=${productId}&nocache=0`
        const upscaledUrl = `${URL.laravelBase}get-upscaled-image/${product.project_id}/${product.image_hash}?ts=${nowTimestamp}`
        const bgremovedUrl = `${URL.laravelBase}get-bgremoved-image/${product.project_id}/${product.image_hash}/${product.rendered_sensitivity || 'medium'}?t=${nowTimestamp}`
        const bgremovedEditedUrl = `${URL.laravelBase}get-bgremoved-edited/${product.project_id}/${product.image_url_hash}?t=${nowTimestamp}`
        const wmremovedUrl = `${URL.laravelBase}get-wmremoved/${product.project_id}/${product.image_url_hash}?t=${nowTimestamp}`
        const wmAutoRemovedUrl = `${URL.laravelBase}get-autowmremoved/${product.project_id}/${product.image_url_hash}?t=${nowTimestamp}`
        const outpaintedUrl = `${URL.laravelBase}get-outpainted-image/${product.project_id}/${product.image_hash}/${sizeString}?t=${nowTimestamp}`

        //console.debug('outpainted url is', outpaintedUrl)

        const inpaintedUrl = product.inpaintedUrl
        const inpaintingMaskUrl = product.inpaintingMaskUrl

        const sourceimageUrl = `${URL.laravelBase}get-source-image/${product.project_id}/${product.image_hash}?t=${timeStamp}${nowTimestamp}`

        const className = 'image-preview shadow-box checker'
        const objects = product.masks_user_set_objects && isJson(product.masks_user_set_objects) ? JSON.parse(product.masks_user_set_objects) : []
        const isManuallyWmRemoved = viewTable === 'watermarks' && objects

        const userId = this.props.project?.user?.id

        switch (imgMode) {
            case "orig":
            case "source":
                return isManuallyWmRemoved ?
                    <StyledSvg version="1.1" viewBox={`0 0 ${product.image_width} ${product.image_height}`} className={'clickable'}>
                        <g>
                            <image
                                className={'shadow-box checker'}
                                width={product.image_width}
                                height={product.image_height}
                                xlinkHref={product.image_url}
                            />
                            <WatermarksRenderSvgObjects objects={objects} color={Theme.blue} opacity={1} />
                        </g>
                    </StyledSvg> :
                    <img
                        src={imgMode === 'source' ? sourceimageUrl : product.image_link}
                        className={`${className}`}
                        /* onClick={() => viewTable === 'watermarks' ? this.props.showModal(idx) : Mergado.openWindow(imgMode === 'source' ? sourceimageUrl : product.image_link)} */
                    />
            case "fie":
                return <img src={previewUrl} className={className} onClick={() => this.props.showModal(idx)} />
            case "other":
                if (typeof product.template !== 'undefined') {
                    const onErrorFunction = (event) => {
                        if (event.currentTarget.parentNode) {
                            event.currentTarget.parentNode.removeChild(event.currentTarget)
                        }
                    }

                    return <StyledOtherImagesDiv>
                        <img
                            src={sourceimageUrl}
                            onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                // don't bubble to the parent GridCell, which opens the product modal wrapper
                                e.stopPropagation()
                                Mergado.openWindow(sourceimageUrl)
                            }}
                            className={className}
                            title={trans('sourceImage')}
                            onError={onErrorFunction}
                        />

                        {(showAll || product.template.type === RENDERED_TYPE_UPSCALE) &&
                            <img
                                src={upscaledUrl}
                                onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                    // don't bubble to the parent GridCell, which opens the product modal wrapper
                                    e.stopPropagation()
                                    Mergado.openWindow(upscaledUrl)
                                }}
                                className={className} title={trans('upscaled_image')}
                                onError={onErrorFunction} />
                        }

                        {(showAll || product.template.type === RENDERED_TYPE_BGREMOVE) &&
                            <React.Fragment>
                                <img
                                    src={bgremovedUrl}
                                    onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                        // don't bubble to the parent GridCell, which opens the product modal wrapper
                                        e.stopPropagation()
                                        Mergado.openWindow(bgremovedUrl)
                                    }}
                                    className={className}
                                    title={trans('bgremoved_image')}
                                    onError={onErrorFunction}
                                />

                                <img
                                    src={bgremovedEditedUrl}
                                    onError={onErrorFunction}
                                    className={`${className}`}
                                    title={trans('bgremoved_edited_image')}
                                    onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                        // don't bubble to the parent GridCell, which opens the product modal wrapper
                                        e.stopPropagation()

                                        modal({
                                            title: <Icon type={'edit-light'} size={28} text={trans("edit")} />,
                                            message: (close) => <Provider store={store}>
                                                <InvertMaskMarker
                                                    product_id={productId}
                                                    image_id={product.image_id}
                                                    form={product.template as Template}
                                                    isAlt={false}
                                                    image_hash={product.image_hash}
                                                    image_url={product.image_url}
                                                    image_url_hash={product.image_url_hash}
                                                    image_width={product.image_width}
                                                    image_height={product.image_height}
                                                    invert_user_set_objects={product.masks_user_set_objects}
                                                    invert_mask_hash={product.masks_mask_hash}
                                                    invert_mask_mode={'paint_transparent'}
                                                    saveObjects={(objects: ObjectsType, mode: InvertMaskModeType) => {
                                                        console.debug('before saveInvertMaskObjects main image')
                                                        saveInvertMaskObjects(product.image_id, product.image_hash, objects, mode, product.image_width, product.image_height, false)
                                                            .then((response: AxiosResponse<any>) => {
                                                                const maskHash = response.data?.maskHash
                                                                const maskMode = response.data?.maskMode
                                                                const userSetObjects = response.data?.userSetObjects
                                                                const urlHash = response.data?.urlHash

                                                                this.updateUserSetObjects(false, urlHash, product.product_id, product.image_id, null, idx, userSetObjects, maskMode, maskHash)
                                                            })
                                                            .catch((e) => {
                                                                console.error('cannot save invert mask', e)
                                                            })
                                                    }}
                                                    closeModal={close}
                                                    idx={idx}
                                                    settingsForm={this.props.settingsForm}
                                                />
                                            </Provider>
                                        })
                                    }}
                                />
                            </React.Fragment>
                        }

                        {isManuallyWmRemoved &&
                            <img
                                src={wmremovedUrl}
                                onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                    // don't bubble to the parent GridCell, which opens the product modal wrapper
                                    e.stopPropagation()
                                    Mergado.openWindow(wmremovedUrl)
                                }}
                                className={className} title={trans('wmremoved_image')}
                                onError={onErrorFunction}
                            />
                        }

                        {(showAll || product.template.type === RENDERED_TYPE_AUTOWMREMOVE) &&
                            <img
                                src={wmAutoRemovedUrl}
                                onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                    // don't bubble to the parent GridCell, which opens the product modal wrapper
                                    e.stopPropagation()
                                    Mergado.openWindow(wmAutoRemovedUrl)
                                }}
                                className={className} title={'autowmremoved_image'}
                                onError={onErrorFunction}
                            />
                        }

                        {(showAll || product.template.type === RENDERED_TYPE_OUTPAINTING) &&
                            <img
                                src={outpaintedUrl}
                                onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                    // don't bubble to the parent GridCell, which opens the product modal wrapper
                                    e.stopPropagation()
                                    Mergado.openWindow(outpaintedUrl)
                                }}
                                className={className}
                                title={'outpainted_image'}
                                onError={onErrorFunction} />
                        }

                        {showAll &&
                            <img
                                src={`${URL.laravelBase}get-outpainted-image/${product.project_id}/${product.image_hash}?t=${nowTimestamp}`}
                                className={className} title={'outpainted_image'}
                                onError={onErrorFunction}
                            />
                        }

                        {(showAll || (product.template.type === RENDERED_TYPE_INPAINTING && product.inpainting_group_id)) &&
                            <React.Fragment>
                                <img
                                    src={inpaintedUrl}
                                    onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                        // don't bubble to the parent GridCell, which opens the product modal wrapper
                                        e.stopPropagation()
                                        Mergado.openWindow(inpaintedUrl)
                                    }}
                                    className={className}
                                    title={'inpainted_image'}
                                    onError={onErrorFunction} />
                                <img
                                    src={inpaintingMaskUrl}
                                    onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                                        // don't bubble to the parent GridCell, which opens the product modal wrapper
                                        e.stopPropagation()
                                        Mergado.openWindow(inpaintingMaskUrl)
                                    }}
                                    className={className}
                                    title={'inpainting_mask_image'}
                                    onError={onErrorFunction} />
                            </React.Fragment>
                        }
                    </StyledOtherImagesDiv>
                }
        }

        return <span />
    }

    getAlternativeImageByImgMode = (alternativeImage: AlternativeImageTypeWithWatermarkMasks, alternativeTemplate: Template, productId: ID, projectId: ID, level: number, idx: number): JSX.Element => {
        const { imgMode, viewTable, sizes, inpaintingGroups } = this.props

        const sourceAltImageUrl = `${URL.laravelBase}get-source-alt-image/${projectId}/${alternativeImage.hash}`
        const previewUrl = alternativeTemplate?.id ? `${URL.laravelBase}alt-images?alt_template_id=${alternativeTemplate?.id}&product_id=${productId}&level=${level}&nocache=0` : null

        const userId = this.props.project?.user?.id

        const objects = alternativeImage?.alt_watermark_masks?.user_set_objects && isJson(alternativeImage.alt_watermark_masks.user_set_objects) ? JSON.parse(alternativeImage.alt_watermark_masks.user_set_objects) : []
        const isManuallyWmRemoved = viewTable === 'watermarks' && objects
        const className = 'alt-image-preview shadow-box checker'

        const nowTimestamp = `${new Date().getTime()}`.substring(8)

        const altTemplateSize = alternativeTemplate && alternativeTemplate.size_id && sizes.isLoaded ? find(sizes.data, (size) => {
            return `${size.id}` === `${alternativeTemplate.size_id}`
        }) : alternativeTemplate?.size

        switch (imgMode) {
            case "orig":
            case "source":
                return isManuallyWmRemoved ?
                    <StyledSvg
                        version="1.1"
                        viewBox={`0 0 ${alternativeImage.width} ${alternativeImage.height}`}
                        className={`${className} clickable`}
                        onClick={() => this.props.showModal(idx, level)}>
                        <g>
                            <image
                                className={'shadow-box checker'}
                                width={alternativeImage.width}
                                height={alternativeImage.height}
                                xlinkHref={alternativeImage.url}
                            />
                            <WatermarksRenderSvgObjects objects={objects} color={Theme.blue} opacity={1} />
                        </g>
                    </StyledSvg> :
                    <img
                        src={imgMode === 'orig' ? alternativeImage.url : sourceAltImageUrl}
                        className={`${className}`}
                        onClick={() => viewTable === 'watermarks' ? this.props.showModal(idx, level) : Mergado.openWindow(sourceAltImageUrl)}
                        title={trans('sourceImage')}
                    />
            case "fie":
                return previewUrl ? <img src={previewUrl} className={className} onClick={() => this.props.showModal(idx, level)} /> : <span />
            case "other":
                if (!alternativeTemplate) {
                    return <span />
                }

                const newSize = getNewSize(alternativeImage.width, alternativeImage.height, {width: altTemplateSize?.width, height: altTemplateSize?.height}, alternativeTemplate.overscan, !!alternativeTemplate.outpaint_over)
                const addSuffix = getAddOutpaintedSuffix(alternativeTemplate.outpaint_over, alternativeTemplate.overscan, alternativeTemplate.translate_x, alternativeTemplate.translate_y)
                const sizeString = `/${newSize?.width}x${newSize?.height}${addSuffix}`

                const showAll = this.props.router.location.search.includes('showAll')
                const upscaledUrl = `${URL.laravelBase}get-upscaled-alt-image/${projectId}/${alternativeImage.hash}?ts=${nowTimestamp}`
                const bgremovedUrl = `${URL.laravelBase}get-bgremoved-alt-image/${projectId}/${alternativeImage.hash}/${alternativeTemplate.effect_sensitivity || 'medium'}?t=${nowTimestamp}`
                const bgremovedEditedUrl = `${URL.laravelBase}get-bgremoved-alt-edited/${projectId}/${alternativeImage.url_hash}?t=${nowTimestamp}`
                const wmremovedUrl = `${URL.laravelBase}get-alt-wmremoved/${projectId}/${alternativeImage.url_hash}?t=${nowTimestamp}`
                const wmAutoRemovedUrl = `${URL.laravelBase}get-alt-autowmremoved/${projectId}/${alternativeImage.url_hash}?t=${nowTimestamp}`
                const outpaintedUrl = `${URL.laravelBase}get-outpainted-alt-image/${projectId}/${alternativeImage.hash}${sizeString}?t=${nowTimestamp}`

                //console.debug('outpainted alt url is', outpaintedUrl)

                const inpaintingGroup = alternativeTemplate.inpainting_group_id && inpaintingGroups.isLoaded ? find(inpaintingGroups.data, (g) => `${g.id}` === `${alternativeTemplate.inpainting_group_id}`) : null
                const position = inpaintingGroup ? getInpaintingGroupPosition(inpaintingGroup) : null

                const inpaintedUrl = inpaintingGroup ? `${URL.laravelBase}get-inpainted-alt-image/${projectId}/${alternativeImage.hash}/${inpaintingGroup.mask_hash}/${position}` : null
                const inpaintingMaskUrl = inpaintingGroup ? `${URL.laravelBase}get-inpainting-mask/${projectId}/${inpaintingGroup.mask_hash}/${position}/${alternativeImage.width}x${alternativeImage.height}` : null

                const onErrorFunction = (event) => {
                    if (event.currentTarget.parentNode) {
                        event.currentTarget.parentNode.removeChild(event.currentTarget)
                    }
                }

                return <Grid cols={'1fr 1fr'}>
                    <GridCell>
                        <img src={sourceAltImageUrl} onClick={() => Mergado.openWindow(sourceAltImageUrl)} className={className} title={trans('sourceImage')} onError={onErrorFunction} />
                    </GridCell>

                    {(showAll || alternativeTemplate.type === RENDERED_TYPE_UPSCALE) &&
                        <GridCell>
                            <img src={upscaledUrl} onClick={() => Mergado.openWindow(upscaledUrl)} className={className} title={trans('upscaled_image')} onError={onErrorFunction} />
                        </GridCell>
                    }

                    {(showAll || alternativeTemplate.type === RENDERED_TYPE_BGREMOVE) &&
                        <React.Fragment>
                            <GridCell>
                                <img src={bgremovedUrl} onClick={() => Mergado.openWindow(bgremovedUrl)} className={className} title={trans('bgremoved_image')} onError={onErrorFunction} />
                            </GridCell>
                            <GridCell>
                                <img
                                    src={bgremovedEditedUrl}
                                    className={`${className}`}
                                    title={trans('bgremoved_edited_image')}
                                    onError={onErrorFunction}
                                    onClick={() => {
                                        modal({
                                            title: <Icon type={'edit-light'} size={28} text={trans("bgremoved_image")} />,
                                            message: (close) => <Provider store={store}>
                                                <InvertMaskMarker
                                                    product_id={productId}
                                                    image_id={alternativeImage.id}
                                                    form={alternativeTemplate as Template}
                                                    isAlt={true}
                                                    image_hash={alternativeImage.hash}
                                                    image_url={alternativeImage.url}
                                                    image_url_hash={alternativeImage.url_hash}
                                                    image_width={alternativeImage.width}
                                                    image_height={alternativeImage.height}
                                                    invert_user_set_objects={alternativeImage.alt_watermark_masks?.user_set_objects}
                                                    invert_mask_hash={alternativeImage.alt_watermark_masks?.mask_hash}  //toto se neaktualizuje ale je potreba pro refresh
                                                    invert_mask_mode={'paint_transparent'}
                                                    saveObjects={(objects: ObjectsType, mode: InvertMaskModeType) => {
                                                        saveInvertMaskObjects(alternativeImage.id, alternativeImage.hash, objects, mode, alternativeImage.width, alternativeImage.height, true)
                                                            .then((response: AxiosResponse<any>) => {
                                                                const maskHash = response.data?.maskHash
                                                                const maskMode = response.data?.maskMode
                                                                const userSetObjects = response.data?.userSetObjects
                                                                const urlHash = response.data?.urlHash
                                                                this.updateUserSetObjects(true, urlHash, productId, alternativeImage.id, level, idx, userSetObjects, maskMode, maskHash)
                                                            })
                                                            .catch((e) => {
                                                                console.error('cannot save invert mask', e)
                                                            })
                                                    }}
                                                    closeModal={close}
                                                    idx={idx}
                                                    settingsForm={this.props.settingsForm}
                                                />
                                            </Provider>
                                        })
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    {showAll &&
                        <GridCell>
                            <img src={wmremovedUrl} onClick={() => Mergado.openWindow(wmremovedUrl)} className={className} title={trans('wmremoved_image')} onError={onErrorFunction} />
                        </GridCell>
                    }

                    {(showAll || alternativeTemplate.type === RENDERED_TYPE_AUTOWMREMOVE) &&
                        <GridCell>
                            <img src={wmAutoRemovedUrl} onClick={() => Mergado.openWindow(wmAutoRemovedUrl)} className={className} title={'autowmremoved_image'} onError={onErrorFunction} />
                        </GridCell>
                    }

                    {(showAll || alternativeTemplate.type === RENDERED_TYPE_OUTPAINTING) &&
                        <GridCell>
                            <img src={outpaintedUrl} onClick={() => Mergado.openWindow(outpaintedUrl)} className={className} title={'outpainted_image'} onError={onErrorFunction} />
                        </GridCell>
                    }

                    {(showAll || (alternativeTemplate.type === RENDERED_TYPE_INPAINTING && alternativeTemplate.inpainting_group_id)) &&
                        <GridCell>
                            {inpaintedUrl && <img src={inpaintedUrl} onClick={() => Mergado.openWindow(inpaintedUrl)} className={className} title={'inpainted_image'} onError={onErrorFunction} />}
                            {inpaintingMaskUrl && <img src={inpaintingMaskUrl} onClick={() => Mergado.openWindow(inpaintingMaskUrl)} className={className} title={'inpainting_mask_image'} onError={onErrorFunction} />}
                        </GridCell>
                    }
                </Grid>
        }

        return <span />
    }

    render() {
        const { products, isLoading, viewTable, paginator, filtersForm, project, findSimilar, alternativeTemplatesLevels, alternativeTemplates } = this.props
        const { clipboardText } = this.state

        if (isLoading) {
            return <SectionLoader />
        }

        const groupNames: ShowGroupType[] = filtersForm.image && filtersForm.image.showGroup && filtersForm.image.showGroup ? filtersForm.image.showGroup : ['all']
        const userId = project.user && project.user.id ? project.user.id : null

        return (
            <StyledGroup key={viewTable}>
                {!isLoading && products.data.products.data &&
                    <StyledGrid>
                        {products.data.products.data.map((product: ExtendedProductType, idx: number) => {
                            const productId: ID = product.product_id
                            const mergadoProductUrl: string = `${MERGADO_APP}/${productId}`
                            const productUrl: string = product.elements_url
                            const previewUrl: string = `${URL.laravelBase}images?template_id=${product.template_id}&product_id=${productId}&nocache=0`

                            const emptyImages: boolean = groupNames.includes('empty_images')
                            const sameImages: boolean = groupNames.includes('same_images')
                            const invalidUrl: boolean = groupNames.includes('invalid_url')

                            const altImagesCount = product.alternative_images?.length
                            const levelsForThisTemplate = this.props.alternativeTemplatesLevels.data.filter((al: AlternativeTemplatesLevels) => `${al.template_id}` === `${product.template_id}`)
                            const altTemplatesByLevels = getAltTemplatesByLevels(levelsForThisTemplate, alternativeTemplates.data, altImagesCount)

                            return <GridCell key={productId + '_' + product.image_id}>
                                <Grid cols={'auto auto auto'}>
                                    <GridCell />

                                    <Grid cols={altTemplatesByLevels.length ? 'auto auto' : 'auto'}>
                                        <GridCell col={'1/-1'}>{product.elements_name}</GridCell>

                                        <GridCell className={'image clickable centering'} style={{ padding: '10px' }} onClick={() => this.props.showModal(idx)}>
                                            {!emptyImages ?
                                                product.image_link !== '' ? this.getImageByImgMode(product, idx) : trans('no_image') :
                                                <div className='item-id' style={{ whiteSpace: 'nowrap' }}>
                                                    <Icon type={'tag'} text={product.elements_item_id} title={"ITEM_ID"} />
                                                </div>
                                            }
                                        </GridCell>

                                        <GridCell className={'image clickable centering'} style={{ padding: '10px' }}>
                                            {product.alternative_images &&
                                                product.alternative_images.map((altImage: AlternativeImageTypeWithWatermarkMasks) => {
                                                    const alternativeTemplate = altTemplatesByLevels[altImage.level] ?? null

                                                    return <div key={altImage.id}>
                                                        {this.getAlternativeImageByImgMode(altImage, alternativeTemplate, product.product_id, product.project_id, altImage.level, idx)}
                                                    </div>
                                                })
                                            }
                                        </GridCell>

                                        {viewTable === 'visual_similarity' &&
                                            <GridCell col={'1/-1'} className={'shadow-box'} style={{ textAlign: 'center', borderRadius: '6px' }}>
                                                <h6>{trans('similar_images')}</h6>
                                                <VisualSimilarityRender product={product} template={product.template} />
                                            </GridCell>
                                        }

                                        {!emptyImages &&
                                            <GridCell col={'1/-1'}>
                                                {product.image_width && product.image_height && <span className={'product-image-size'}>{product.image_width}x{product.image_height}</span>}
                                                {product.image_content_type && <span className={'product-image-mime'}> [{product.image_content_type}]</span>}
                                                {product.image_bg_color && <span style={{ marginLeft: '15px' }}><Icon className={'shadow-box'} style={{ borderRadius: '50%' }} type={'circle'} color={product.image_bg_color} size={24} /></span>}
                                            </GridCell>
                                        }

                                        {product.template &&
                                            <GridCell col={'1/-1'}>
                                                <Grid cols={altTemplatesByLevels.length ? 'auto auto' : 'auto'}>
                                                    <GridCell style={{alignSelf: altTemplatesByLevels.length ? 'center !important' : undefined}}>
                                                        {alternativeTemplates ?
                                                            <div title={`${product.template.name} ${getTemplateTypeTranslated(product.template.type)}`}>
                                                                <TemplateBadges template={product.template} showIcons={true} showText={false} />
                                                            </div> :
                                                            <React.Fragment>
                                                                <div><Badge secondary={true}>{product.template.name}</Badge></div>
                                                                <div><TemplateBadges template={product.template} showIcons={true} /></div>
                                                            </React.Fragment>
                                                        }
                                                    </GridCell>

                                                    {altTemplatesByLevels &&
                                                        <GridCell>
                                                            {altTemplatesByLevels.map((a: AlternativeTemplate, level: number) => {
                                                                if (!a) {
                                                                    return null
                                                                }

                                                                const alt: AlternativeTemplatesLevels | null = alternativeTemplatesLevels && alternativeTemplatesLevels.data.length > 0 && product.template_id ?
                                                                    find(alternativeTemplatesLevels.data, (atl: AlternativeTemplatesLevels) => `${atl.template_id}` === `${product.template_id}` && `${atl.alternative_template_id}` === `${a.id}` && a.active === 1) :
                                                                    null

                                                                if (!alt) {
                                                                    return null;
                                                                }

                                                                const i = `${trans(alt.levels_mode)}${alt.levels ? ` ${alt.levels}` : ''}`

                                                                const text = getTemplateTypeTranslated(a.type)

                                                                return <div key={`aa_${alt.id}`} title={`${level}: **${a.name}** ${i} ${text}`}>
                                                                    <TemplateBadges template={a} showInline={false} showIcons={true} showText={false} />
                                                                </div>
                                                            })
                                                            }
                                                        </GridCell>
                                                    }
                                                </Grid>
                                            </GridCell>
                                        }

                                        <GridCell col={'1/-1'}>
                                            <Grid className={'links'} cols={'repeat(auto-fit, minmax(32px, 1fr))'}>
                                                {!emptyImages && previewUrl &&
                                                    <GridCell className='clickable'>
                                                        <div onClick={() => Mergado.openWindow(previewUrl)}>
                                                            <Icon type={'feedimageeditor'} title={trans("link_fie")} size={20} />
                                                        </div>
                                                    </GridCell>
                                                }
                                                {mergadoProductUrl &&
                                                    <GridCell className='clickable'>
                                                        <div onClick={() => Mergado.openWindow(mergadoProductUrl)}>
                                                            <Icon type={'mergado'} title={trans("link_mergado")} size={20} />
                                                        </div>
                                                    </GridCell>
                                                }
                                                {!emptyImages && product.image_link &&
                                                    <GridCell className='clickable'>
                                                        <div onClick={() => Mergado.openWindow(product.image_link)}>
                                                            <Icon type={'image-light'} title={trans("image")} size={20} />
                                                        </div>
                                                    </GridCell>
                                                }
                                                {productUrl &&
                                                    <GridCell className='clickable'>
                                                        <div onClick={() => Mergado.openWindow(productUrl)}>
                                                            <Icon type={'shopping-basket'} color={DarkGrayColor} title={trans("link_eshop")} size={20} />
                                                        </div>
                                                    </GridCell>
                                                }
                                                {!emptyImages && product.image_link &&
                                                    <GridCell>
                                                        <FindSimilarButton imageHash={product.image_hash} />
                                                    </GridCell>
                                                }
                                                <GridCell title={`item id ${product.elements_item_id}`}>
                                                    <Icon type={'tag'} size={iconSize} />
                                                </GridCell>
                                            </Grid>


                                            {(allowSuperadminForUsers.includes(userId) || isGodModeOn) &&
                                                <div className="some-users-only" style={{ overflow: 'hidden', fontSize: 'small', border: '1px dashed #dbd49b', margin: '3px' }}>
                                                    <div>
                                                        {product.rendered_type && <Badge small={true} title={'rendered_type'} secondary={true}>rendered {getTemplateTypeTranslated(product.rendered_type)}</Badge>}
                                                        {product.should_redownload && <Badge small={true} color={Theme.red} secondary={false}>should redownload {product.should_redownload_priority}</Badge>}
                                                        {product.should_rerender && <Badge small={true} color={Theme.red} secondary={false}>should rerender {product.should_rerender_priority}</Badge>}
                                                        {product.image_hash &&
                                                            <span
                                                                className={'clickable'}
                                                                title={`image hash ${product.image_hash}`}
                                                                onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                                                                    navigator.clipboard.writeText(product.image_hash)
                                                                    this.setState({ clipboardText: product.image_hash })
                                                                }}>
                                                                <Icon type={'clipboard'} size={20} color={clipboardText === product.image_hash ? '#ccc' : Theme.blue} text={'hash'} />
                                                            </span>
                                                        }
                                                    </div>

                                                    {!!product.image_is_transparent && <Badge color="yellow">is transparent</Badge>}
                                                    {!!product.validations_has_alpha && <Badge color="orange">has alpha</Badge>}
                                                    {(product.image_color_space_string && product.image_color_space_string !== 'sRGB') && <Badge color="blue">{product.image_color_space_string}</Badge>}

                                                    {(product.rendered_by || product.rendered_at) && <div title={`rendered ${product.rendered_hash}`}>{product.rendered_by} • {product.rendered_at}</div>}
                                                    {product.render_error && <div title={'render_error'}>{product.render_error}</div>}
                                                    <div title={'downloaded_at'}>downloaded at: {product.downloaded_at}</div>
                                                    <div title={'product, template, image'}>{product.product_id} • {product.template_id} • {product.image_id}</div>
                                                    <div title={'alt_images_count'}>alt images count: {altImagesCount}</div>

                                                    {!! (product.alternative_images && product.alternative_images.length) &&
                                                        <Grid cols={'repeat(5, auto)'}>
                                                            <GridCell>level</GridCell>
                                                            <GridCell>alt tmpl</GridCell>
                                                            <GridCell>by</GridCell>
                                                            <GridCell>at</GridCell>
                                                            <GridCell>t</GridCell>

                                                            {product.alternative_images
                                                                .filter(a => a.alternative_template_id)
                                                                .map(a => <React.Fragment key={`${a.level}_${product.product_id}`}>
                                                                    <GridCell>{a.level}</GridCell>
                                                                    <GridCell>{a.alternative_template_id}</GridCell>
                                                                    <GridCell>{a.render ? a.render.rendered_by : '-'}</GridCell>
                                                                    <GridCell>{a.render ? a.render.rendered_at : '-'}</GridCell>
                                                                    <GridCell>{a.render ? a.render.rendered_type : '-'}</GridCell>
                                                                </React.Fragment>)}
                                                        </Grid>
                                                    }

                                                    {renderAltLevels(getAltTemplateLevelsByLevel(levelsForThisTemplate, product.alternative_images?.length), alternativeTemplates.data, `/eshop/1/project/${product.project_id}/templates/${product.template_id}/edit-alt/`)}
                                                </div>
                                            }
                                        </GridCell>
                                    </Grid>

                                    <GridCell />
                                </Grid>
                            </GridCell>
                        })}
                    </StyledGrid>
                }

                {paginator}
            </StyledGroup>
        )
    }
}

const StyledGroup = css('div')({
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' img.image-preview': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100px',
        maxHeight: '100px',
        margin: '5px',
    },
    ' img.alt-image-preview': {
        width: 'auto',
        height: 'auto',
        maxWidth: '40px',
        maxHeight: '40px',
        margin: '5px',
    },
    ' .tags-wrapper > span': {
        display: 'inline-block',
        marginTop: '10px',
        borderRadius: Theme.radius,
    }
})

const StyledGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gridGap: '10px',
    ' .muk-gridcell': {
        alignSelf: 'end',
    },
})

const StyledSvg = css('svg')({
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
})

const StyledOtherImagesDiv = css('div')({
})

function mapStateToProps(state) {
    return {
        products: state.products,
        project: state.project.data,
        inpaintingGroups: state.inpaintingGroups,
        alternativeTemplatesLevels: state.alternativeTemplatesLevels,
        alternativeTemplates: state.alternativeTemplates,
        sizes: state.sizes,
        findSimilar: state.findSimilar,
        filtersForm: selectFormValues(state, 'filtersForm'),
        settingsForm: selectFormValues(state, 'settingsForm'),
    }
}

export default connect(mapStateToProps, {
    setFindSimilar,
    setProductsToStore,
    setInpaintingGroupsToStore,
})(withRouter(ProductTableGrid))

